<template>
	<teleport :to="teleportId">
		<transition :css="false" appear @appear="onAppear" @enter="onEnter" @leave="onLeave">
			<div v-if="isToastOpen" class="app-toast">
				<div ref="backdropRef" class="app-toast__backdrop" />

				<div ref="contentRef" class="app-toast__content">
					<div class="app-toast__message">
						<slot v-bind="{ close }">
							{{ message }}
						</slot>
					</div>

					<div class="app-toast__action">
						<slot name="action" v-bind="{ close }" />

						<base-button icon title="Close" class="app-toast__close-button" @click="close">
							<template #icon>
								<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M18 6L6 18M6 6l12 12" stroke="#201E1E" stroke-linecap="round" stroke-linejoin="round"/></svg>
							</template>
						</base-button>
					</div>
				</div>
			</div>
		</transition>
	</teleport>
</template>

<script lang="ts">
import { gsap } from 'gsap/all';
import { defineComponent } from 'vue';

import { Popup } from '@/common/popup';
import { usePopup } from '@/common/popup/src';

import { ToastSystem } from '../ToastSystem';

export default defineComponent({
	props: {
		isOpen: {
			type: Boolean,
		},

		message: {
			type: String,
			default: '',
		},
	},

	emits: ['update:isOpen', 'close'],

	setup(props, { emit }) {
		const toast = new Popup(props.isOpen);
		const { isOpen: isToastOpen, teleportId } = usePopup(toast, ToastSystem, props, emit);

		return {
			toast,
			isToastOpen,
			teleportId,

			open: () => toast.open(),
			close: () => toast.close(),
		};
	},

	mounted() {
		this.open();
	},

	methods: {
		getElements(el: Element) {
			return {
				content: el.querySelector('.app-toast__content'),
				backdrop: el.querySelector('.app-toast__backdrop'),
			};
		},

		onAppear(el: Element, done: () => void) {
			const { content, backdrop } = this.getElements(el);
			const tl = gsap.timeline({
				onComplete: done,
			});

			tl.fromTo(
				backdrop,
				{
					alpha: 0,
					scale: 0.9,
				},
				{
					duration: 0.4,
					alpha: 1,
					scale: 1,
					ease: 'back.out',
				},
			);
			tl.fromTo(
				content,
				{
					alpha: 0,
				},
				{
					duration: 0.4,
					delay: -0.1,
					alpha: 1,
					ease: 'power4.out',
				},
			);
		},

		onEnter(el: Element, done: () => void) {
			this.onAppear(el, done);
		},

		onLeave(el: Element, done: () => void) {
			const { content, backdrop } = this.getElements(el);
			const tl = gsap.timeline({
				onComplete: () => {
					done();
					this.$emit('close');
				},
			});

			tl.to(
				content,
				{ alpha: 0, duration: 0.3, pointerEvents: 'none', ease: 'power4.out' },
				'content',
			);
			tl.to(
				backdrop,
				{
					duration: 0.5,
					width: 0,
					x: (this.$el as HTMLElement).offsetWidth / 2,
					ease: 'power4.out',
				},
				'backdrop-out',
			);
			tl.to(
				backdrop,
				{
					duration: 0.3,
					alpha: 0,
					delay: 0.1,
					ease: 'power4.out',
				},
				'backdrop-out',
			);
		},
	},
});
</script>

<style lang="scss">
.app-toast {
	display: flex;
	align-items: center;
	min-height: 8rem;
	position: fixed;
	right: 4.8rem;
	bottom: 3.2rem;
	left: 4.8rem;
	z-index: 10000;
	color: $color-light;

	&__backdrop {
		@include dropshadow-selected;
		width: 100%;
		height: 100%;
		position: absolute;
		top: 0;
		left: 0;
		z-index: 1;
		border-radius: 1.6rem;
		background: $color-gray;
	}

	&__content {
		display: flex;
		align-items: center;
		width: 100%;
		padding: 1.6rem 1.6rem 1.6rem 2.5rem;
		position: relative;
		z-index: 10;
		font-size: $font-size-h3;
	}

	&__message {
		margin-right: 4rem;
	}

	&__action {
		display: flex;
		align-items: center;
		margin-left: auto;
	}

	&__close-button {
		width: 4.8rem;
		height: 4.8rem;
		color: $color_light;
		border-color: $color_light;
	}

	.base-button {
		height: 4.8rem;
		margin-left: 2.4rem;
		color: $color_light;
		border-color: $color_light;

		&:hover {
			border-color: $color-dark;
		}
	}
}
</style>
